import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { parse } from "query-string";

import I18n from "../../I18n";

import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { ActionButton, Dropdown, TextInput } from "colbi_web_ui/lib/components/inputs";

import { setLocale } from "../../store/actions";

import styles from "./LoginLegacy.module.sass";

import { GlobalProvider } from "../../state/globalProvider";

import { UserRoles } from "../../enums/userRoles";
import configsByTheme from "../../enums/configsByTheme";
import { useAuth } from "react-oidc-context";
import { validateEmail } from "../../utils/queryUtils";
import { decodeUserSession } from "../../utils/utils";

import { AUTH_MODES } from "colbi_web_ui/lib/components/SSO/SSOComponent";
import { getConfigVariable } from "../../config/config";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const LoginLegacy = (props) => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("loginHint") && validateEmail(urlParams.get("loginHint"))) {
        window.localStorage.setItem("loginHint", urlParams.get("loginHint"));
        window.history.replaceState({}, document.title, window.location.pathname);
    }
    const loginHint = window.localStorage.getItem("loginHint");

    const [username, setUsername] = useState(
        loginHint && loginHint.trim().toLowerCase() !== "null" ? loginHint : "",
    );

    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [hideForgot, setHideForgot] = useState(true);
    const [hideForgetPasswordMessage, setHideForgetPasswordMessage] = useState(true);

    const [appTheme] = useState(
        getConfigVariable("REACT_APP_COLBI_THEME")
            ? getConfigVariable("REACT_APP_COLBI_THEME").toLocaleLowerCase()
            : document.localStorage.getItem("theme")
              ? document.localStorage.getItem("theme")
              : "sovos",
    );

    const [isSovosIdentity, setAccountSovosIdentity] = useState(
        getConfigVariable("REACT_APP_AUTH_MODE") === AUTH_MODES.HYBRID,
    );

    const [loginApplicationName, setApplicationName] = useState(
        configsByTheme(appTheme)["loginApplicationName"],
    );
    const [localesByAppTheme, setLocales] = useState(configsByTheme(appTheme)["locales"]);

    useEffect(() => {
        setApplicationName(configsByTheme(appTheme)["loginApplicationName"]);
        setLocales(configsByTheme(appTheme)["locales"]);
        const root = document.getElementsByTagName("html")[0];
        root.setAttribute("class", appTheme);
    }, [appTheme]);

    const handleForgetPassword = (e) => {
        e.preventDefault();
        props.forgetPassword(email);
        setEmail("");
        setTimeout(() => {
            setHideForgetPasswordMessage(false);
        }, 500);
    };

    const root = document.getElementsByTagName("html")[0];
    root.setAttribute("class", appTheme);

    const { user, loginError, fetch, forgetPasswordMessage, match, history, location } = props;

    const locale = match.params.locale || configsByTheme(appTheme)["defaultLocale"] || "en";
    const i18n = I18n.use(locale);
    setLocale(locale);

    const emailServiceActive = (fetch.results[`en_email_service_active`] || {}).active;
    localStorage.setItem("theme", appTheme);
    let auth = useAuth();
    const userInformation = decodeUserSession(user);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (username && username.trim().toLowerCase() !== "null") {
            window.localStorage.setItem("loginHint", username);
        }
        if (getConfigVariable("REACT_APP_AUTH_MODE") === AUTH_MODES.LEGACY || !isSovosIdentity) {
            props.login(username, password);
        } else {
            props
                .checkValidSovosIdentity(username)
                .then((valid) => {
                    if (valid && auth) {
                        const queryParams = {
                            login_hint: username,
                        };
                        if (appTheme && appTheme !== "sovos") {
                            queryParams.extraQueryParams = {
                                theme_key: appTheme,
                            };
                        }
                        auth.signinRedirect(queryParams);
                    } else {
                        setAccountSovosIdentity(false);
                    }
                })
                .catch((err) => {
                    setAccountSovosIdentity(false);
                });
        }
    };

    if (userInformation) {
        const organizationsUser = Object.keys(userInformation.organizationsRoles || {});
        const { redirect } = parse(location.search);
        const defaultOrgToNavigate = organizationsUser?.length ? organizationsUser[0] : null;
        return (
            <Redirect
                to={
                    redirect || parseInt(userInformation.expired)
                        ? `/${locale}/reset_password`
                        : userInformation.role === UserRoles.BACKOFFICE
                          ? `/${locale}/settings/`
                          : defaultOrgToNavigate
                            ? `/${locale}/organizations/${defaultOrgToNavigate}`
                            : `/${locale}/`
                }
            />
        );
    }

    return (
        <Provider
            {...{
                i18n,
                location,
            }}
        >
            <div className={styles["root"]}>
                <div className={styles["background"]} />
                <div className={styles["login"]}>
                    <div className={styles["company-logo"]} />
                    {loginApplicationName ? (
                        <h2 className={styles["title"]}>{loginApplicationName}</h2>
                    ) : null}

                    {hideForgot ? (
                        <div className={styles["login-container"]}>
                            {!isSovosIdentity ? (
                                <div className={styles["details"]}>
                                    {loginError && (
                                        <div className={styles["error"]}>{loginError}</div>
                                    )}
                                    <form
                                        className={styles["form"]}
                                        onSubmit={(e) => {
                                            handleSubmit(e);
                                        }}
                                    >
                                        <TextInput
                                            className={styles["login-text-input"]}
                                            type="text"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            name={i18n`Email`}
                                            placeholder={i18n`Your Email`}
                                            required
                                            value={username}
                                            onChange={(e) => {
                                                setUsername(e.target.value);
                                            }}
                                        />
                                        <TextInput
                                            className={styles["login-text-input"]}
                                            type="password"
                                            autoComplete="off"
                                            name={i18n`Password`}
                                            placeholder={i18n`Your password`}
                                            required
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                            }}
                                        />
                                        <div className={styles["login-actions"]}>
                                            <Dropdown
                                                value={locale.toUpperCase()}
                                                placeholder={""}
                                                options={localesByAppTheme}
                                                i18n={i18n}
                                                className={styles["dropdown"]}
                                                onChange={(e) => {
                                                    history.push(
                                                        `/${e.target.value.id || "en"}/login`,
                                                    );
                                                    setLocale(e.target.value.id);
                                                }}
                                            />

                                            <ActionButton
                                                icon="login-arrow"
                                                primary
                                                className={styles["action-button"]}
                                                type="submit"
                                            >
                                                {i18n`Sign in`}
                                            </ActionButton>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <div className={styles["details"]}>
                                    {" "}
                                    {loginError && (
                                        <div className={styles["error"]}>{loginError}</div>
                                    )}
                                    <form
                                        autoComplete="on"
                                        className={styles["form"]}
                                        onSubmit={(e) => {
                                            handleSubmit(e);
                                        }}
                                    >
                                        <TextInput
                                            className={styles["login-text-input"]}
                                            type="text"
                                            autoComplete="on"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            name={i18n`Email`}
                                            placeholder={i18n`Your Email`}
                                            required
                                            value={username}
                                            onChange={(e) => {
                                                setUsername(e.target.value);
                                            }}
                                        />
                                        <div className={styles["login-actions"]}>
                                            <div></div>
                                            <ActionButton
                                                icon="login-arrow"
                                                primary
                                                className={styles["action-button"]}
                                                type="submit"
                                            >
                                                {i18n`Sign in`}
                                            </ActionButton>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    ) : null}
                    <div className={` ${styles["container-sidebar"]}`}>
                        {hideForgot && !isSovosIdentity ? (
                            <p
                                className={styles["item-link"]}
                                onClick={() => {
                                    setHideForgot(false);
                                }}
                            >
                                {i18n`Forgot your password?`}
                            </p>
                        ) : null}
                        <div
                            className={` ${styles["container-sidebar"]} ${
                                hideForgot ? styles["hide"] : "show"
                            } `}
                        >
                            {!emailServiceActive ? (
                                <div>
                                    <h2
                                        className={styles["title"]}
                                    >{i18n`Forgot your password?`}</h2>
                                    <p className={styles["subtitle"]}>
                                        {configsByTheme(appTheme)["contactEmailPerTheme"]
                                            ? i18n`recover-password-message-${
                                                  configsByTheme(appTheme)["contactEmailPerTheme"]
                                              }`
                                            : i18n`You should ask your system admin to reset your password.`}
                                    </p>
                                    <p
                                        className={styles["item-link"]}
                                        onClick={() => {
                                            setHideForgot(true);
                                            setHideForgetPasswordMessage(false);
                                        }}
                                    >
                                        {i18n`Understood!`}
                                    </p>
                                </div>
                            ) : (
                                <div className={styles["container-sidebar"]}>
                                    <div className={styles["details"]}>
                                        {!hideForgetPasswordMessage && forgetPasswordMessage && (
                                            <div
                                                className={
                                                    forgetPasswordMessage.success
                                                        ? styles["success"]
                                                        : styles["error"]
                                                }
                                            >
                                                {i18n`${forgetPasswordMessage.message}`}
                                            </div>
                                        )}
                                        <form
                                            className={styles["form"]}
                                            onSubmit={(e) => {
                                                handleForgetPassword(e);
                                            }}
                                        >
                                            <h2
                                                className={styles["title"]}
                                            >{i18n`Forgot your password?`}</h2>
                                            <TextInput
                                                type="text"
                                                autoComplete="off"
                                                autoCorrect="off"
                                                autoCapitalize="off"
                                                spellCheck="false"
                                                placeholder={i18n`Your email`}
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    setHideForgetPasswordMessage(true);
                                                }}
                                            />
                                            <div className={styles["reset-actions"]}>
                                                <p
                                                    className={styles["item-link"]}
                                                    onClick={() => {
                                                        setHideForgot(true);
                                                        setHideForgetPasswordMessage(true);
                                                    }}
                                                >
                                                    {i18n`Back!`}
                                                </p>
                                                <ActionButton
                                                    primary
                                                    icon="forget-password"
                                                    className={styles["login-action-button"]}
                                                    type="submit"
                                                >
                                                    {i18n`Reset Password`}
                                                </ActionButton>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Provider>
    );
};

LoginLegacy.queries = (args) => [
    {
        resource: "email_service_active",
        body: "active",
        forceRefresh: true,
    },
];

export default LoginLegacy;
